<template>
  <div v-if="hasData || loading || showErrorTile">
    <template v-if="!showErrorTile">
      <Card
        :loadingState="loading"
        v-for="(dataBlock, dbIdx) in databoxData"
        :key="dataBlock.title + dataBlock.subtitle + dbIdx"
        :title="dataBlock.isFallback ? dataBlock.title : ''"
      >
        <template v-if="dataBlock.title !== ''" #content>
          <span v-if="dataBlock.isFallback" v-dompurify-html="dataBlock.content"> </span>
          <Collapser
            v-else
            :collapserTitle="dataBlock.title"
            :collapserSubTitle="dataBlock.subtitle"
            :expanded="false"
            id="motor.toggle-details"
            trackId="toggleDetails"
          >
            <template #collapserContent>
              <template v-if="!dataBlock.isFallback">
                <div v-for="(dataSection, idx) in dataBlock.datablock" :key="idx">
                  <DataBox
                    :data="[dataSection]"
                    action-link-icon-class="icon-reload"
                    link-type="link2"
                  ></DataBox>
                </div>
              </template>
            </template>
          </Collapser>
        </template>
      </Card>
    </template>

    <!-- Error Tile-->
    <ErrorTile
      v-else-if="showErrorTile"
      :title="$t('motorDetails.errorTitle')"
      :showFooter="false"
    ></ErrorTile>
  </div>
</template>

<script setup lang="ts">
import { onMounted, type Ref, ref } from 'vue'
import { Card, Collapser, type DataBlock, DataBox } from 'de-zurich-ui-library'
import ErrorTile from '@/components/common/ErrorTile.vue'
import { navigate } from '@/utils/NavigationUtils'
import { useMotorStore } from '@/stores/motorStore'
import { usePoliciesStore } from '@/stores/policiesStore'
import { type MotorContract } from '@/types/motor/MotorContract'
import { type EMobilContract, isEMobilContract } from '@/types/emobil/EMobilContract'
import { EMobilDetailsFactory } from '@/components/emobil/EMobilDetailsFactory'
import { isTechInsuranceNonEBikeContract } from '@/utils/motor/TechInsuranceUtils'
import { t } from '@/i18n'
import type { Policy } from 'policies-api'
import type { DataBoxWithHeader } from '@/utils/shur/SHURDataboxData'

const props = defineProps({
  policyId: {
    type: String
  }
})

const loading = ref(true)
const showErrorTile = ref(false)
const hasData = ref(false)
const policy: Ref<Policy | null> = ref(null)

const databoxData: Ref<DataBoxWithHeader[]> = ref([
  {
    title: '',
    subtitle: '',
    datablock: [],
    isFallback: false
  }
])
const contract: Ref<EMobilContract | null> = ref(null)

onMounted(async () => {
  loading.value = true
  showErrorTile.value = false
  hasData.value = false

  // Fetch policy first to check if it's TechInsurance
  try {
    policy.value = await usePoliciesStore().getPolicyByPolicyId(props.policyId ?? '')
  } catch (error) {
    console.error('Error fetching policy:', error)
  }

  let response: MotorContract | EMobilContract | null = null
  try {
    response = await useMotorStore().getMotorContractByPolicyId(props.policyId ?? '')
  } catch (error) {
    handleLoadError(error)
    return
  }

  if (!response || !isEMobilContract(response)) {
    doShowErrorTile()
    return
  }

  contract.value = response

  // Check if this is a TechInsurance non-E-Bike contract
  if (isTechInsuranceNonEBikeContract(response, policy.value ?? undefined)) {
    // Create generic card like TRANSPORT_UNKNOWN in SHUR
    const genericCard: DataBoxWithHeader = {
      title: t('techInsuranceDetails.title').toString(),
      subtitle: '',
      content: t('techInsuranceDetails.content').toString(),
      datablock: [],
      isFallback: true
    }
    databoxData.value = [genericCard]
    hasData.value = true
    loading.value = false
    return
  }

  prepareDataboxData()

  loading.value = false
})

function handleLoadError(error: any) {
  loading.value = false

  if (error.response && error.response.status === 401) {
    // Unauthenticated call
    // redirect to login page
    navigate(globalThis.ENV_CONFIG?.REDIRECTS.LOGIN_PAGE)
  } else {
    doShowErrorTile()
  }
}

function doShowErrorTile() {
  contract.value = null
  showErrorTile.value = true
}

function prepareDataboxData(): void {
  // clear previous databoxData
  databoxData.value = []

  const factory = new EMobilDetailsFactory()
  const eMobilDataBlocks: DataBlock[] = []
  factory.createDetails(contract.value, eMobilDataBlocks)

  // Convert DataBlock[] to DataBoxWithHeader[] format
  if (eMobilDataBlocks.length > 0) {
    const header: DataBoxWithHeader = {
      title: t('motorDetails.collapserTitle').toString(),
      subtitle: '',
      datablock: eMobilDataBlocks,
      isFallback: false
    }
    databoxData.value = [header]
    hasData.value = true
  }
}
</script>

<style scoped lang="scss">
.is-styled .list__wrapper div.hoverable-icon.hoverable-icon-span {
  display: flex;
}
</style>
